export interface StreamChatCallbacks {
    onText: (text: string) => void;
    onFinish: (content: string, traceId?: string) => void;
    onError: (message: string) => void;
}

export interface StreamChatOptions {
    url: string;
    body: object;
    callbacks: StreamChatCallbacks;
    signal?: AbortSignal;
}

interface SSEEventData {
    type: string;
    text?: string;
    content?: string;
    message?: string;
    traceId?: string;
}

/**
 * Stream AI assistant chat responses via SSE (POST-based fetch).
 * Parses `data: {...}\n\n` events and dispatches to typed callbacks.
 */
export async function streamChat({
    url,
    body,
    callbacks,
    signal,
}: StreamChatOptions): Promise<void> {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Parse complete SSE events (delimited by \n\n)
            let boundary: number;
            while ((boundary = buffer.indexOf("\n\n")) !== -1) {
                const rawEvent = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);

                // Extract data from "data: ..." lines
                const dataLine = rawEvent
                    .split("\n")
                    .find((line) => line.startsWith("data: "));
                if (!dataLine) continue;

                const jsonStr = dataLine.slice(6); // strip "data: "
                let event: SSEEventData;
                try {
                    event = JSON.parse(jsonStr);
                } catch {
                    continue; // skip malformed events
                }

                switch (event.type) {
                    case "text":
                        callbacks.onText(event.text ?? "");
                        break;
                    case "finish":
                        callbacks.onFinish(event.content ?? "", event.traceId);
                        break;
                    case "error":
                        callbacks.onError(event.message ?? "Unknown error");
                        break;
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
